import { groupBy, isEmpty, isNumber, sum } from 'lodash';
import moment from 'moment';
import { ROLE_PERMISSIONS } from '../constants';
import { ACTION_TYPES, FAULT_TYPES, GRAPH_TYPES } from '../constants/protocol';
import { useEffect } from 'react';

export const convertCelsiusToFahrenheit = (temp) => {
  if (temp == null) return null;
  if (temp || temp === 0) {
    return Math.round(temp * (9 / 5) + 32);
  }
  return 0;
};

export const getSelectedSnowThreshold = (flatSwitch, isF) => {
  const thresholds = new Set();

  const collect = (machine) => {
    if (machine?.isSelected) {
      thresholds.add(machine?.snowSensor?.defaultTemp ?? null);
    }
  };

  Object.values(flatSwitch ?? {}).forEach((locationData) => {
    Object.values(locationData ?? {}).forEach((entry) => {
      if (entry?.machineType || entry?.deviceMac) {
        collect(entry);
      } else {
        Object.values(entry ?? {}).forEach(collect);
      }
    });
  });

  if (thresholds.size !== 1) return null;

  const [temp] = [...thresholds];
  if (typeof temp !== 'number') return null;

  return isF ? convertCelsiusToFahrenheit(temp) : temp;
};

export const convertFahrenheitToCelsius = (temp) => {
  if (temp == null) return null;
  if (temp || temp === 0) {
    return (temp - 32) * (5 / 9);
  }
  return 0;
};

/**
 * A stored °C temperature in the operator's unit, rounded the way the screens
 * have always shown it: °F to the nearest degree, °C down to the whole degree.
 * The store keeps the device's raw °C so a °F setting survives the round trip.
 * @param {number|null|undefined} celsius
 * @param {boolean} isF
 * @returns {number|null}
 */
export const toDisplayTemp = (celsius, isF) => {
  if (celsius == null) return null;
  return isF ? convertCelsiusToFahrenheit(celsius) : Math.floor(celsius);
};

/**
 * A temperature the operator typed, in their unit, as °C for the store and
 * the backend. Empty input stays null.
 * @param {number|string|null|undefined} value
 * @param {boolean} isF
 * @returns {number|null}
 */
export const fromInputTemp = (value, isF) => {
  if (value == null || value === '') return null;
  const temp = Number(value);
  return isF ? convertFahrenheitToCelsius(temp) : temp;
};

/**
 * A saved master-control command stores its temperatures as text in the unit
 * it was saved in, e.g. "active 1000°F". These keep the two sides in step.
 */
export const savedCommandTemp = (celsius, isF) =>
  `${toDisplayTemp(celsius, isF)}°${isF ? 'F' : 'C'}`;

export const savedCommandTempToCelsius = (text, isF) =>
  fromInputTemp(text.match(/\d+/)[0], isF);

export const hasValue = (value) => {
  return value !== null && value !== undefined && value !== '';
};

export const formatTime = ({ date, time }, isTimestamp) => {
  const { hour, minute, division } = time;

  const m = moment(date);

  m.hour(parseInt(hour));
  m.minute(parseInt(minute));
  m.second(0);

  if (division === 'pm') {
    m.add(12, 'hours');
  }

  if (isTimestamp) {
    return m.utc().valueOf();
  }

  const formattedDate = m.utc().format('YYYY-MM-DDTHH:mm:ss') + 'Z';

  return formattedDate;
};

export const getStartAndEndTimestamps = (timestamp, unit) => {
  const date = moment(timestamp).utc();

  return {
    start: date.clone().startOf(unit?.slice(0, -1)).valueOf(),
    end: date.clone().endOf(unit?.slice(0, -1)).valueOf(),
  };
};

export const readableTime = (time) => {
  const date = moment(time).utc();
  const hour = date.format('hh');
  const minute = date.format('mm');
  const division = date.format('a');

  const timeObject = {
    date: moment(time).utc().startOf('day')._d,
    time: {
      hour,
      minute,
      division,
    },
  };

  return timeObject;
};

export const isImageFile = (file) => {
  const fileName = file.name;
  const fileExtension = fileName.split('.').pop().toLowerCase();
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp'];
  return imageExtensions.includes(fileExtension);
};

export const getTitle = (gender) => {
  if (gender) {
    if (gender.toLowerCase() === 'male') {
      return 'Mr.';
    } else if (gender.toLowerCase() === 'female') {
      return 'Ms.';
    } else {
      return '';
    }
  }
  return '';
};

export const getLocationsMachinesName = (deviceIds, switches) => {
  const locationsMachines = [];

  deviceIds.map((deviceId) => {
    Object.values(switches).map((zone, zoneIndex) => {
      Object.values(zone).map((machine, machineIndex) => {
        if (machine.deviceMac === deviceId) {
          const locationName = Object.keys(switches)[zoneIndex];
          const machineName = Object.keys(zone)[machineIndex];
          locationsMachines.push([locationName, machineName]);
        }
      });
    });
  });

  const locationsMachinesGrouped = groupBy(locationsMachines, (el) => el[0]);
  const locations = Object.keys(locationsMachinesGrouped);
  const machines = Object.values(locationsMachinesGrouped).map((el) =>
    el.map((el) => el[1]),
  );

  return { locations, machines };
};

export const isAnotherSystemRunning = (currentRunning, anotherSystem) => {
  if (currentRunning === null) {
    return false;
  }
  if (currentRunning === anotherSystem) {
    return true;
  }
  return false;
};

export const calculateTotalEnergyConsumption = (value, swtName, isF) => {
  const consumptionGas = isF ? value : Math.round(value * 0.0283168);
  const consumption = swtName === 'tgs' ? consumptionGas : value;
  return consumption ?? '--';
};

export const getFaultIndexes = (data) => {
  let indexFault = [];
  data?.forEach((fault, idx) => {
    if (fault === 1) {
      indexFault.push(idx + 1);
    }
  });
  return indexFault;
};

export const getFaultsList = (data, specificLocation) => {
  return Object.values(data)
    .map((location) => {
      return Object.values(location).map((machineData) => {
        const defaultData = {
          number: [],
          locationPre: 'mbta',
          location: machineData.zone_id,
          machine: machineData.deviceMac,
          address: machineData.locationAddress,
          date: '',
        };
        let faults = [];
        if (machineData.ssrFault?.includes(1)) {
          getFaultIndexes(machineData.ssrFault).forEach((faultIndex) => {
            const time = Object.values(machineData.ssrState)[faultIndex - 1]
              ?.fault_time;
            faults.push({
              ...defaultData,
              faultType: FAULT_TYPES.SSR,
              number: [faultIndex],
              date: time ? moment.unix(time).format('h:mma - DD/MM/YYYY') : '',
            });
          });
        }
        if (machineData.srrOverCurrent?.includes(1)) {
          getFaultIndexes(machineData.srrOverCurrent).forEach((faultIndex) => {
            const time = Object.values(machineData.ssrState)[faultIndex - 1]
              ?.fault_time;
            faults.push({
              ...defaultData,
              faultType: FAULT_TYPES.SSR_OVER_CURRENT,
              number: [faultIndex],
              date: time ? moment.unix(time).format('h:mma - DD/MM/YYYY') : '',
            });
          });
        }
        if (machineData.groundFault === 1) {
          faults.push({
            ...defaultData,
          });
        }
        if (
          machineData.thermocoupleFault &&
          machineData.thermocoupleFault?.includes(1)
        ) {
          getFaultIndexes(machineData.thermocoupleFault).forEach(
            (faultIndex) => {
              faults.push({
                ...defaultData,
                faultType: FAULT_TYPES.THERMOCOUPLE,
                number: [faultIndex],
              });
            },
          );
        }
        if (machineData.bmsFault === 1) {
          faults.push({
            ...defaultData,
            faultType: FAULT_TYPES.BMS,
          });
        }
        if (machineData.hplpFault === 1) {
          faults.push({
            ...defaultData,
            faultType: FAULT_TYPES.HPLP,
          });
        }
        if (machineData.timeoutFault === 1) {
          faults.push({
            ...defaultData,
            faultType: FAULT_TYPES.TIMEOUT,
          });
        }
        if (machineData.thermocoupleFault === 1) {
          faults.push({
            ...defaultData,
            faultType: FAULT_TYPES.THERMOCOUPLE,
          });
        }
        return faults;
      });
    })
    .flat(2);
};

export const electricalFaultsList = (data) => {
  return Object.values(data)
    .map((location) => {
      return Object.values(location).map((machineData) => {
        let faults = [];
        if (machineData.ssrFault?.includes(1)) {
          getFaultIndexes(machineData.ssrFault).forEach((faultIndex) => {
            const time = Object.values(machineData.ssrState)[faultIndex - 1]
              ?.fault_time;
            faults.push({
              faultType: FAULT_TYPES.SSR,
              number: [faultIndex],
              locationPre: 'mbta',
              location: machineData.locationId,
              machine: machineData.deviceMac,
              address: machineData.locationAddress,
              date: time ? moment.unix(time).format('h:mma - DD/MM/YYYY') : '',
            });
          });
        }
        if (machineData.srrOverCurrent?.includes(1)) {
          getFaultIndexes(machineData.srrOverCurrent).forEach((faultIndex) => {
            const time = Object.values(machineData.ssrState)[faultIndex - 1]
              ?.fault_time;
            faults.push({
              faultType: FAULT_TYPES.SSR_OVER_CURRENT,
              number: [faultIndex],
              locationPre: 'mbta',
              location: machineData.locationId,
              machine: machineData.deviceMac,
              address: machineData.locationAddress,
              date: time ? moment.unix(time).format('h:mma - DD/MM/YYYY') : '',
            });
          });
        }
        if (machineData.groundFault === 1) {
          faults.push({
            faultType: FAULT_TYPES.GROUND,
            number: [],
            locationPre: 'mbta',
            location: machineData.locationId,
            machine: machineData.deviceMac,
            address: machineData.locationAddress,
            date: '',
          });
        }
        if (machineData.thermocoupleFault?.includes(1)) {
          getFaultIndexes(machineData.thermocoupleFault).forEach(
            (faultIndex) => {
              faults.push({
                faultType: FAULT_TYPES.THERMOCOUPLE,
                number: [faultIndex],
                locationPre: 'mbta',
                location: machineData.locationId,
                machine: machineData.deviceMac,
                address: machineData.locationAddress,
                date: '',
              });
            },
          );
        }
        return faults;
      });
    })
    .flat(2);
};

export const gasFaultsList = (data) => {
  return Object.values(data)
    .map((location) => {
      return Object.values(location).map((machineData) => {
        let faults = [];
        if (machineData.bmsFault === 1) {
          faults.push({
            faultType: FAULT_TYPES.BMS,
            number: [],
            locationPre: 'mbta',
            location: machineData.locationId,
            machine: machineData.deviceMac,
            address: machineData.locationAddress,
            date: '',
          });
        }
        if (machineData.hplpFault === 1) {
          faults.push({
            faultType: FAULT_TYPES.HPLP,
            number: [],
            locationPre: 'mbta',
            location: machineData.locationId,
            machine: machineData.deviceMac,
            address: machineData.locationAddress,
            date: '',
          });
        }
        if (machineData.timeoutFault === 1) {
          faults.push({
            faultType: FAULT_TYPES.TIMEOUT,
            number: [],
            locationPre: 'mbta',
            location: machineData.locationId,
            machine: machineData.deviceMac,
            address: machineData.locationAddress,
            date: '',
          });
        }
        if (machineData.thermocoupleFault === 1) {
          faults.push({
            faultType: FAULT_TYPES.THERMOCOUPLE,
            number: [],
            locationPre: 'mbta',
            location: machineData.locationId,
            machine: machineData.deviceMac,
            address: machineData.locationAddress,
            date: '',
          });
        }
        return faults;
      });
    })
    .flat(2);
};

export const extractUniquePropertyValues = (arr, prop) => {
  const uniqueValues = new Set();

  arr.forEach((item) => {
    if (item.hasOwnProperty(prop)) {
      uniqueValues.add(item[prop]);
    }
  });
  return Array.from(uniqueValues);
};

export const getLocationDataByDeviceId = (deviceId, swtStatus) => {
  for (const location in swtStatus) {
    if (
      swtStatus[location].devices?.hasOwnProperty(deviceId) ||
      swtStatus[location].hasOwnProperty(deviceId)
    ) {
      return swtStatus[location] || {};
    }
  }
  return {};
};

export const getESSAuditTrailFaults = (data, swtStatus) => {
  const faults = [];
  if (!data || !swtStatus) return [];
  data?.forEach(({ row, ...restProps }) => {
    row?.deviceIds?.forEach((deviceId) => {
      if (
        row?.newData?.hasOwnProperty('ssr_fault') &&
        row.newData?.ssr_fault?.includes(1)
      ) {
        faults.push({
          faultType: 'ssr fault',
          number: getFaultIndexes(row.newData?.ssr_fault),
          locationPre: 'mbta',
          location: getLocationDataByDeviceId(deviceId, swtStatus)?.locationId,
          machine: deviceId,
          address: getLocationDataByDeviceId(deviceId, swtStatus)
            ?.locationAddress,
          date: moment(row.createdAt).format('h:mma - DD/MM/YYYY'),
          attends: row.userAttends,
          position: row.newData?.position,
          isLCD: row.isLCD,
        });
      }

      if (
        row?.newData?.hasOwnProperty('thermocouple_fault') &&
        row.newData?.thermocouple_fault?.includes(1)
      ) {
        faults.push({
          faultType: 'thermocouple failure',
          number: getFaultIndexes(row.newData?.thermocouple_fault),
          locationPre: 'mbta',
          location: getLocationDataByDeviceId(deviceId, swtStatus)?.locationId,
          machine: deviceId,
          address: getLocationDataByDeviceId(deviceId, swtStatus)
            ?.locationAddress,
          date: moment(row.createdAt).format('h:mma - DD/MM/YYYY'),
          attends: row.userAttends,
          position: row.newData?.position,
          isLCD: row.isLCD,
        });
      }
      if (
        row?.newData?.hasOwnProperty('ground_fault') &&
        row.newData?.ground_fault == 1
      ) {
        faults.push({
          faultType: 'ground fault',
          number: [],
          locationPre: 'mbta',
          location: getLocationDataByDeviceId(deviceId, swtStatus)?.locationId,
          machine: deviceId,
          address: getLocationDataByDeviceId(deviceId, swtStatus)
            ?.locationAddress,
          date: moment(row.createdAt).format('h:mma - DD/MM/YYYY'),
          attends: row.userAttends,
          position: undefined,
          isLCD: row.isLCD,
        });
      }

      if (
        row?.newData?.hasOwnProperty('srr_over_current') &&
        row.newData?.srr_over_current?.includes(1)
      ) {
        faults.push({
          faultType: 'ssr over current',
          number: getFaultIndexes(row.newData?.srr_over_current),
          locationPre: 'mbta',
          location: getLocationDataByDeviceId(deviceId, swtStatus)?.locationId,
          machine: deviceId,
          address: getLocationDataByDeviceId(deviceId, swtStatus)
            ?.locationAddress,
          date: moment(row.createdAt).format('h:mma - DD/MM/YYYY'),
          attends: row.userAttends,
          position: row.newData?.position,
          isLCD: row.isLCD,
        });
      }
    });
  });
  return faults;
};

export const getTESAuditTrailFaults = (data, swtStatus) => {
  const faults = [];
  if (!data || !swtStatus) return [];
  data?.forEach(({ row, ...restProps }) => {
    row?.deviceIds?.forEach((deviceId) => {
      const locationData = getLocationDataByDeviceId(deviceId, swtStatus);
      let locationid;
      if (locationData?.parent_location_id) {
        locationid = locationData.parent_location_id;
      } else {
        locationid = locationData?.locationId;
      }
      if (
        row?.newData?.hasOwnProperty('e_ssr_fault') &&
        row.newData?.e_ssr_fault?.includes(1)
      ) {
        faults.push({
          faultType: 'ssr fault',
          number: getFaultIndexes(row.newData?.e_ssr_fault),
          locationPre: 'mbta',
          location: locationid,
          machine: deviceId,
          address: getLocationDataByDeviceId(deviceId, swtStatus)
            ?.locationAddress,
          date: moment(row.createdAt).format('h:mma - DD/MM/YYYY'),
          attends: row.userAttends,
          position: row.newData?.position,
          isLCD: row.isLCD,
        });
      }

      if (
        row?.newData?.hasOwnProperty('e_thermocouple_fault') &&
        row.newData?.e_thermocouple_fault?.includes(1)
      ) {
        faults.push({
          faultType: 'thermocouple failure',
          number: getFaultIndexes(row.newData?.e_thermocouple_fault),
          locationPre: 'mbta',
          location: locationid,
          machine: deviceId,
          address: getLocationDataByDeviceId(deviceId, swtStatus)
            ?.locationAddress,
          date: moment(row.createdAt).format('h:mma - DD/MM/YYYY'),
          attends: row.userAttends,
          position: row.newData?.position,
          isLCD: row.isLCD,
        });
      }
      if (
        row?.newData?.hasOwnProperty('e_ground_fault') &&
        row.newData?.e_ground_fault == 1
      ) {
        faults.push({
          faultType: 'ground fault',
          number: [],
          locationPre: 'mbta',
          location: locationid,
          machine: deviceId,
          address: getLocationDataByDeviceId(deviceId, swtStatus)
            ?.locationAddress,
          date: moment(row.createdAt).format('h:mma - DD/MM/YYYY'),
          attends: row.userAttends,
          position: undefined,
          isLCD: row.isLCD,
        });
      }

      if (
        row?.newData?.hasOwnProperty('e_srr_over_current') &&
        row.newData?.e_srr_over_current?.includes(1)
      ) {
        faults.push({
          faultType: 'ssr over current',
          number: getFaultIndexes(row.newData?.e_srr_over_current),
          locationPre: 'mbta',
          location: locationid,
          machine: deviceId,
          address: getLocationDataByDeviceId(deviceId, swtStatus)
            ?.locationAddress,
          date: moment(row.createdAt).format('h:mma - DD/MM/YYYY'),
          attends: row.userAttends,
          position: row.newData?.position,
          isLCD: row.isLCD,
        });
      }
    });
  });
  return faults;
};
export const getTgsAuditTrailFaults = (data, swtStatus) => {
  const faults = [];
  if (!data || !swtStatus) return [];
  data?.forEach(({ row, ...restProps }) => {
    row?.deviceIds?.forEach((deviceId) => {
      if (
        row?.newData?.hasOwnProperty('bms_fault') &&
        row.newData?.bms_fault == 1
      ) {
        faults.push({
          faultType: 'bms fault',
          number: [],
          locationPre: 'mbta',
          location: getLocationDataByDeviceId(deviceId, swtStatus)?.locationId,
          machine: deviceId,
          address: getLocationDataByDeviceId(deviceId, swtStatus)
            ?.locationAddress,
          date: moment(row.createdAt).format('h:mma - DD/MM/YYYY'),
          attends: row.userAttends,
          position: undefined,
          isLCD: row.isLCD,
        });
      }

      if (
        row?.newData?.hasOwnProperty('hplp_fault') &&
        row.newData?.hplp_fault == 1
      ) {
        faults.push({
          faultType: 'hp/lp fault',
          number: [],
          locationPre: 'mbta',
          location: getLocationDataByDeviceId(deviceId, swtStatus)?.locationId,
          machine: deviceId,
          address: getLocationDataByDeviceId(deviceId, swtStatus)
            ?.locationAddress,
          date: moment(row.createdAt).format('h:mma - DD/MM/YYYY'),
          attends: row.userAttends,
          position: undefined,
          isLCD: row.isLCD,
        });
      }
      if (
        row?.newData?.hasOwnProperty('thermocouple_fault') &&
        row.newData?.thermocouple_fault == 1
      ) {
        faults.push({
          faultType: 'thermocouple fault',
          number: [],
          locationPre: 'mbta',
          location: getLocationDataByDeviceId(deviceId, swtStatus)?.locationId,
          machine: deviceId,
          address: getLocationDataByDeviceId(deviceId, swtStatus)
            ?.locationAddress,
          date: moment(row.createdAt).format('h:mma - DD/MM/YYYY'),
          attends: row.userAttends,
          position: undefined,
          isLCD: row.isLCD,
        });
      }

      if (
        row?.newData?.hasOwnProperty('timeout_fault') &&
        row.newData?.timeout_fault == 1
      ) {
        faults.push({
          faultType: 'timeout fault',
          number: [],
          locationPre: 'mbta',
          location: getLocationDataByDeviceId(deviceId, swtStatus)?.locationId,
          machine: deviceId,
          address: getLocationDataByDeviceId(deviceId, swtStatus)
            ?.locationAddress,
          date: moment(row.createdAt).format('h:mma - DD/MM/YYYY'),
          attends: row.userAttends,
          position: undefined,
          isLCD: row.isLCD,
        });
      }
    });
  });
  return faults;
};

// The same command can be logged twice: once with the user id and once
// without it. Keep every row, carrying the user id and LCD user name over
// to the row that lacks them.
const fillDuplicateCommandRows = (data) => {
  const userIdByCommand = {};
  const lcdUserNameByCommand = {};
  const commandsWithoutUser = new Set();
  data.forEach(({ row }) => {
    const commandNumber = row.newData?.commandNumber;
    if (!hasValue(commandNumber)) return;
    if (hasValue(row.userId)) userIdByCommand[commandNumber] = row.userId;
    else commandsWithoutUser.add(commandNumber);
    if (hasValue(row.lcdUserName))
      lcdUserNameByCommand[commandNumber] = row.lcdUserName;
  });

  return data.map((item) => {
    const commandNumber = item.row.newData?.commandNumber;
    if (!commandsWithoutUser.has(commandNumber)) return item;
    return {
      ...item,
      row: {
        ...item.row,
        userId: item.row.userId ?? userIdByCommand[commandNumber],
        lcdUserName:
          item.row.lcdUserName ?? lcdUserNameByCommand[commandNumber],
      },
    };
  });
};

export const auditTrailActionsDataForamt = (data, swtStatus, isF, allUsers) => {
  if (!data || !swtStatus) return [];
  const unit = isF ? `°F` : `°C`;
  return fillDuplicateCommandRows(data).map(({ row, ...restProps }) => {
    const actionLoctaionIds = [
      ...new Set(
        row.deviceIds?.map(
          (deviceId) =>
            getLocationDataByDeviceId(deviceId, swtStatus)?.locationId,
        ) || [],
      ),
    ];

    const actionSwitches = {};
    row.deviceIds?.forEach((deviceId) => {
      const locationId = getLocationDataByDeviceId(
        deviceId,
        swtStatus,
      )?.locationId;
      if (actionSwitches.hasOwnProperty(locationId)) {
        actionSwitches[locationId].push(deviceId);
      } else {
        actionSwitches[locationId] = [deviceId];
      }
    });
    let userData = allUsers.find((user) => user.user_id === row.userId);

    const defaultData = {
      companyName: 'ATA',
      actionType: row.actionType,
      date: new Date(row.createdAt),
      userName: userData?.username,
      userCode: userData?.user_code,
      system: row.deviceType,
      locations: actionLoctaionIds,
      switches: actionSwitches,
      machines: row.deviceIds,
      isLCD: row.isLCD,
      userId: row.userId,
      lcdUserName: row.lcdUserName,
      parameters: {},
    };

    const isTelemetryOverview =
      row?.actionType === ACTION_TYPES.GLOBAL_TELEMETRY_OVERVIEW ||
      row?.actionType === ACTION_TYPES.GLOBAL_DATA_TELEMETRY_OVERVIEW;

    if (isTelemetryOverview || row?.actionType === ACTION_TYPES.AUDIT_TRAIL) {
      return {
        ...defaultData,
        parameters: {
          commandNumber: row.newData?.commandNumber,
          ...(isTelemetryOverview && {
            globalTelemetryOverview: row.newData,
          }),
          ...(row?.actionType === ACTION_TYPES.AUDIT_TRAIL && {
            auditTrail: {
              startDate: moment(row.newData.startDate).format('h:mma M/D/YYYY'),
              endDate: moment(row.newData.endDate).format('h:mma M/D/YYYY'),
            },
          }),
        },
      };
    }

    if (
      row?.actionType === ACTION_TYPES.SETTING ||
      row?.actionType === ACTION_TYPES.HEAT_CONFIGURATION
    ) {
      return {
        ...defaultData,
        parameters: {
          commandNumber: row.newData?.commandNumber,
          ...(row.newData.hasOwnProperty('EBP_mode') && {
            EBP_mode: row.newData.EBP_mode,
          }),
          ...(row.newData.hasOwnProperty('e_EBP_mode') && {
            EBP_mode: row.newData.e_EBP_mode,
          }),
          ...(row.newData.hasOwnProperty('TES_enabled') && {
            TES_enabled: row.newData.TES_enabled,
          }),
          ...(row.newData.hasOwnProperty('blower_snow_threshold') && {
            blower_snow_threshold: row.newData.blower_snow_threshold,
          }),
          ...(row.newData.hasOwnProperty('electrical_snow_threshold') && {
            electrical_snow_threshold: row.newData.electrical_snow_threshold,
          }),
          ...(row.newData.hasOwnProperty('blower_temp_ch') && {
            blower_temp_ch: row.newData.blower_temp_ch,
          }),
          ...(row.newData.hasOwnProperty('burner_temp_ch') && {
            burner_temp_ch: row.newData.burner_temp_ch,
          }),
          ...(row.newData.hasOwnProperty('display_temp_ch') && {
            display_temp_ch: row.newData.display_temp_ch,
          }),
          ...(row.newData.hasOwnProperty('enclosure_temp_ch') && {
            enclosure_temp_ch: row.newData.enclosure_temp_ch,
          }),
          ...(row.newData.hasOwnProperty('outside_temp_ch') && {
            outside_temp_ch: row.newData.outside_temp_ch,
          }),
          ...(row.newData.hasOwnProperty('tc_mode') && {
            tc_mode: row.newData.tc_mode,
          }),
          ...(row.newData.hasOwnProperty('wind_threshold') && {
            wind_threshold: row.newData.wind_threshold,
          }),
          ...(row.newData.hasOwnProperty('gas_type') && {
            gas_type: row.newData.gas_type,
          }),
          ...(row.newData.hasOwnProperty('force_run') && {
            force: row.newData.force_run,
          }),
          ...(row.newData.hasOwnProperty('force') && {
            force: row.newData.force,
          }),
          ...(row.newData.hasOwnProperty('initial_open') && {
            initial_open: row.newData.initial_open,
          }),
          ...(row.newData.hasOwnProperty('min_open') && {
            min_open: row.newData.min_open,
          }),
          ...(row.newData.hasOwnProperty('max_open') && {
            max_open: row.newData.max_open,
          }),
        },
      };
    }

    return {
      ...defaultData,
      ...(row.deviceType === 'ESS'
        ? {
            parameters: {
              ...(row.newData.hasOwnProperty('on_switch') && {
                instantHeat: row.newData.on_switch
                  ? `active ${
                      row.newData.hasOwnProperty('instant_temp') &&
                      (isF
                        ? convertCelsiusToFahrenheit(row.newData.instant_temp)
                        : row.newData.instant_temp)
                    }${unit}`
                  : 'off',
              }),
              ...(!row.newData.hasOwnProperty('on_switch') &&
                row.newData.hasOwnProperty('instant_temp') &&
                row.prevData?.hasOwnProperty('instant_temp') && {
                  instantHeatChangeTemp: `from ${row.prevData.instant_temp}${unit} to ${row.newData.instant_temp}${unit}`,
                }),
              ...(row.newData.hasOwnProperty('on_constant') && {
                optionalConstantTemp: row.newData.on_constant
                  ? `ready ${
                      row.newData.constant_temp && isF
                        ? convertCelsiusToFahrenheit(row.newData.constant_temp)
                        : row.newData.constant_temp
                    }${unit}`
                  : 'off',
              }),
              ...(!row.newData.hasOwnProperty('on_constant') &&
                row.newData.hasOwnProperty('constant_temp') &&
                row.prevData?.hasOwnProperty('constant_temp') && {
                  optionalConstantTempChanged: `from ${row.prevData.constant_temp}${unit} to ${row.newData.constant_temp}${unit}`,
                }),
              ...((row.newData.hasOwnProperty('snow_enabled') ||
                row.newData.hasOwnProperty('snow_trigger')) && {
                snowSensor:
                  row.newData.snow_enabled || row.newData.snow_trigger
                    ? row.newData.snow_enabled
                      ? 'ready'
                      : 'active'
                    : 'off',
              }),
              ...(row.newData.hasOwnProperty('scheduleAdd') && {
                heatingSchedule: row.newData.scheduleAdd?.startDate
                  ? `ready ${
                      isF
                        ? convertCelsiusToFahrenheit(
                            row.newData.scheduleAdd.threshold,
                          )
                        : row.newData.scheduleAdd.threshold
                    }${unit}` +
                    ` | ${moment(row.newData.scheduleAdd.startDate).format(
                      'h:mma M/D/YYYY',
                    )}` +
                    ` | ${moment(row.newData.scheduleAdd.endDate).format(
                      'h:mma M/D/YYYY',
                    )}`
                  : 'off',
              }),
              ...(row.prevData?.hasOwnProperty('scheduleDelete') && {
                scheduleEnabled: 'off',
              }),
              ...(row.newData.hasOwnProperty('schedule_enabled') && {
                scheduleEnabled:
                  row.newData.schedule_enabled === 1 ? 'enabled' : 'disabled',
              }),
              ...((row.newData.hasOwnProperty('wind_enabled') ||
                row.newData.hasOwnProperty('wind_trigger')) && {
                windFactor:
                  row.newData.wind_enabled || row.newData.wind_trigger
                    ? row.newData.wind_enabled
                      ? 'ready'
                      : 'active'
                    : 'off',
              }),
              ...(row.newData.hasOwnProperty('freeze') && {
                freeze: row.newData.freeze ? 'on' : 'off',
              }),
              ...(row.newData.hasOwnProperty('device_active') && {
                deviceActive: row.newData.device_active ? 'on' : 'off',
              }),
              ...(row.newData.hasOwnProperty('EBP') && {
                EBP: row.newData.EBP,
              }),
              ...(row?.actionType === ACTION_TYPES.SSR_RESET && {
                ssrReset: true,
              }),
              ...(row?.actionType === ACTION_TYPES.GROUND_RESET && {
                groundReset: true,
              }),
              ...(row?.actionType === ACTION_TYPES.THERMOCOUPLE_RESET &&
                (row.newData.hasOwnProperty('position') ||
                  row.newData.hasOwnProperty('index')) && {
                  thermocoupleReset: true,
                }),
              ...(row?.actionType === ACTION_TYPES.THERMOCOUPLE_RESET &&
                row.newData.hasOwnProperty('thermocouple') && {
                  thermocoupleForce: row.newData.thermocouple.fault_mode,
                }),
              ...(row?.actionType === ACTION_TYPES.GLOBAL_TELEMETRY_OVERVIEW && {
                globalTelemetryOverview: row.newData,
              }),
              commandNumber: row.newData?.commandNumber,
            },
          }
        : row.deviceType === 'TES'
          ? {
              parameters: {
                ...(row.newData.hasOwnProperty('e_on_switch') && {
                  instantHeat: row.newData.e_on_switch
                    ? `active ${
                        row.newData.hasOwnProperty('e_instant_temp') &&
                        (isF
                          ? convertCelsiusToFahrenheit(
                              row.newData.e_instant_temp,
                            )
                          : row.newData.e_instant_temp)
                      }${unit}`
                    : 'off',
                }),
                ...(row.newData.hasOwnProperty('on_switch') && {
                  instantHeat: row.newData.on_switch
                    ? `active ${
                        row.newData.hasOwnProperty('instant_temp') &&
                        (isF
                          ? convertCelsiusToFahrenheit(row.newData.instant_temp)
                          : row.newData.instant_temp)
                      }${unit}`
                    : 'off',
                }),
                ...(!row.newData.hasOwnProperty('on_switch') &&
                  row.newData.hasOwnProperty('e_instant_temp') &&
                  row.prevData?.hasOwnProperty('e_instant_temp') && {
                    instantHeatChangeTemp: `from ${row.prevData.e_instant_temp}${unit} to ${row.newData.e_instant_temp}${unit}`,
                  }),
                ...(row.newData.hasOwnProperty('e_on_constant') && {
                  optionalConstantTemp: row.newData.e_on_constant
                    ? `ready ${
                        row.newData.e_constant_temp && isF
                          ? convertCelsiusToFahrenheit(
                              row.newData.e_constant_temp,
                            )
                          : row.newData.e_constant_temp
                      }${unit}`
                    : 'off',
                }),
                ...(!row.newData.hasOwnProperty('e_on_constant') &&
                  row.newData.hasOwnProperty('e_constant_temp') &&
                  row.prevData?.hasOwnProperty('e_constant_temp') && {
                    optionalConstantTempChanged: `from ${row.prevData.e_constant_temp}${unit} to ${row.newData.e_constant_temp}${unit}`,
                  }),
                ...((row.newData.hasOwnProperty('e_snow_enabled') ||
                  row.newData.hasOwnProperty('e_snow_trigger')) && {
                  snowSensor:
                    row.newData.e_snow_enabled || row.newData.e_snow_trigger
                      ? row.newData.e_snow_enabled
                        ? 'ready'
                        : 'active'
                      : 'off',
                }),
                ...(row.newData.hasOwnProperty('scheduleAdd') && {
                  heatingSchedule: row.newData.scheduleAdd?.startDate
                    ? `ready ${
                        isF
                          ? convertCelsiusToFahrenheit(
                              row.newData.scheduleAdd.threshold,
                            )
                          : row.newData.scheduleAdd.threshold
                      }${unit}` +
                      ` | ${moment(row.newData.scheduleAdd.startDate).format(
                        'h:mma M/D/YYYY',
                      )}` +
                      ` | ${moment(row.newData.scheduleAdd.endDate).format(
                        'h:mma M/D/YYYY',
                      )}`
                    : 'off',
                }),
                ...(row.prevData?.hasOwnProperty('scheduleDelete') && {
                  scheduleEnabled: 'off',
                }),
                ...(row.newData.hasOwnProperty('e_schedule_enabled') && {
                  scheduleEnabled:
                    row.newData.e_schedule_enabled === 1
                      ? 'enabled'
                      : 'disabled',
                }),
                ...((row.newData.hasOwnProperty('e_wind_enabled') ||
                  row.newData.hasOwnProperty('e_wind_trigger')) && {
                  windFactor:
                    row.newData.e_wind_enabled || row.newData.e_wind_trigger
                      ? row.newData.e_wind_enabled
                        ? 'ready'
                        : 'active'
                      : 'off',
                }),
                ...(row.newData.hasOwnProperty('e_freeze') && {
                  freeze: row.newData.e_freeze ? 'on' : 'off',
                }),
                ...(row.newData.hasOwnProperty('device_active') && {
                  deviceActive: row.newData.device_active ? 'on' : 'off',
                }),
                ...(row.newData.hasOwnProperty('EBP') && {
                  EBP: row.newData.EBP,
                }),
                ...(row?.actionType === ACTION_TYPES.SSR_RESET && {
                  ssrReset: true,
                }),
                ...(row?.actionType === ACTION_TYPES.GROUND_RESET && {
                  groundReset: true,
                }),
                ...(row?.actionType === ACTION_TYPES.THERMOCOUPLE_RESET &&
                  (row.newData.hasOwnProperty('position') ||
                    row.newData.hasOwnProperty('index')) && {
                    thermocoupleReset: true,
                  }),
                ...(row?.actionType === ACTION_TYPES.THERMOCOUPLE_RESET &&
                  row.newData.hasOwnProperty('thermocouple') && {
                    thermocoupleForce: row.newData.thermocouple.fault_mode,
                  }),
                commandNumber: row.newData?.commandNumber,
              },
            }
          : {
              parameters: {
                ...(row.newData.hasOwnProperty('on_switch') && {
                  instantHeat: row.newData.on_switch
                    ? `active ${
                        row.newData.instant_temp &&
                        (isF
                          ? convertCelsiusToFahrenheit(row.newData.instant_temp)
                          : row.newData.instant_temp)
                      }${unit}`
                    : 'off',
                }),
                ...(!row.newData.hasOwnProperty('on_switch') &&
                  row.newData.hasOwnProperty('instant_temp') &&
                  row.prevData?.hasOwnProperty('instant_temp') && {
                    instantHeatChangeTemp: `from ${row.prevData.instant_temp}${unit} to ${row.newData.instant_temp}${unit}`,
                  }),
                ...((row.newData.hasOwnProperty('snow_enabled') ||
                  row.newData.hasOwnProperty('snow_trigger')) && {
                  snowSensor:
                    row.newData.snow_enabled || row.newData.snow_trigger
                      ? row.newData.snow_enabled
                        ? 'ready'
                        : 'active'
                      : 'off',
                }),
                ...(row.newData.hasOwnProperty('scheduleAdd') && {
                  heatingSchedule: row.newData.scheduleAdd?.startDate
                    ? `ready ${
                        isF
                          ? convertCelsiusToFahrenheit(
                              row.newData.scheduleAdd.threshold,
                            )
                          : row.newData.scheduleAdd.threshold
                      }${unit}` +
                      ` | ${moment(row.newData.scheduleAdd.startDate).format(
                        'h:mma M/D/YYYY',
                      )}` +
                      ` | ${moment(row.newData.scheduleAdd.endDate).format(
                        'h:mma M/D/YYYY',
                      )}`
                    : 'off',
                }),
                ...(row.prevData?.hasOwnProperty('scheduleDelete') && {
                  scheduleEnabled: 'off',
                }),
                ...(row.newData.hasOwnProperty('schedule_enabled') && {
                  scheduleEnabled:
                    row.newData.schedule_enabled === 1 ? 'enabled' : 'disabled',
                }),
                ...((row.newData.hasOwnProperty('wind_enabled') ||
                  row.newData.hasOwnProperty('wind_trigger')) && {
                  windFactor:
                    row.newData.wind_enabled || row.newData.wind_trigger
                      ? row.newData.wind_enabled
                        ? 'ready'
                        : 'active'
                      : 'off',
                }),
                ...(row.newData.hasOwnProperty('freeze') && {
                  freeze: row.newData.freeze ? 'on' : 'off',
                }),
                ...(row.newData.hasOwnProperty('device_active') && {
                  deviceActive: row.newData.device_active ? 'on' : 'off',
                }),
                ...(row.newData.hasOwnProperty('fan') && {
                  fan: row.newData.fan ? 'on' : 'off',
                }),
                ...(row.newData.hasOwnProperty('EBP') && {
                  EBP: row.newData.EBP,
                }),
                ...(row?.actionType === ACTION_TYPES.SSR_RESET && {
                  ssrReset: true,
                }),
                ...(row?.actionType === ACTION_TYPES.GROUND_RESET && {
                  groundReset: true,
                }),
                ...(row?.actionType === ACTION_TYPES.HPLP_RESET && {
                  Hp_LpReset: true,
                }),
                ...(row?.actionType === ACTION_TYPES.TIMEOUT_RESET && {
                  timeOutReset: true,
                }),
                ...(row?.actionType === ACTION_TYPES.THERMOCOUPLE_RESET &&
                  (row.newData.hasOwnProperty('position') ||
                    row.newData.hasOwnProperty('index')) && {
                    thermocoupleReset: true,
                  }),
                ...(row?.actionType === ACTION_TYPES.THERMOCOUPLE_RESET &&
                  row.newData.hasOwnProperty('thermocouple') && {
                    thermocoupleForce: row.newData.thermocouple.fault_mode,
                  }),
                commandNumber: row.newData?.commandNumber,
              },
            }),
    };
  });
};

export const auditTrailSettingsDataForamt = (data) => {
  if (!data || !Array.isArray(data) || data.length === 0) return [];

  const sortedData = data.sort((a, b) => a.row.userId - b.row.userId);

  const userLoginLogs = [];

  for (let i = 0; i < sortedData.length; i++) {
    const currentRow = sortedData[i].row;
    const logObj = { login: null, logout: null };

    if (currentRow?.newData.login === true) {
      if (
        i < sortedData.length - 1 &&
        sortedData[i + 1].row.newData.login === false
      ) {
        logObj.login = new Date(sortedData[i + 1].row.createdAt);
        logObj.logout = new Date(currentRow.createdAt);
        i++;
      } else {
        logObj.login = new Date(currentRow.createdAt);
        logObj.logout = new Date(sortedData[i - 1].row.createdAt);
      }
    } else if (currentRow?.newData.login === false) {
      if (i > 0 && sortedData[i - 1]?.row.newData.login === true) {
        logObj.login = new Date(currentRow.createdAt);
        logObj.logout = new Date(sortedData[i - 1].row.createdAt);
      } else {
        logObj.login = new Date(sortedData[i + 1].row.createdAt);
        logObj.logout = new Date(currentRow.createdAt);
      }
    }

    userLoginLogs.push(logObj);
  }
  const filteredLogs = userLoginLogs.filter((el, i, arr) => {
    if (i === 0) return true;
    const prev = arr[i - 1];
    if (!el.login && !el.logout) return false;
    return (
      el.login?.getTime() !== prev.login?.getTime() &&
      el.logout?.getTime() !== prev.logout?.getTime()
    );
  });
  return filteredLogs;
};
export const getDeviceIdsByLocationData = (locations) => {
  const deviceIds = [];
  Object.values(locations).forEach((location) => {
    deviceIds.push(...Object.keys(location));
  });
  const result = [...new Set(deviceIds)];
  return result;
};

export const getLocationAssignedUsersByDeviceIds = (
  deviceIds,
  allLocations,
) => {
  const assignedUsers = [];
  deviceIds.forEach((deviceId) => {
    assignedUsers
      .push
      ();
  });
  return [...new Set(assignedUsers.flat())];
};

export const machineAuditTrailActionsFormat = (
  actionsArray,
  allUsersArray,
  isF,
) => {
  if (!actionsArray) return [];
  const unit = isF ? `°F` : `°C`;
  return actionsArray?.map(({ row, ...restProps }) => {
    let userData = allUsersArray.find((user) => user?.user_id === row?.userId);
    let number = row.newData?.commandNumber ?? 0;
    let commandNumber = `${createCommandTitle(
      row?.actionType,
      row?.deviceType,
      row?.isLCD,
    )}-${userData.user_code}-${moment(row.createdAt).format('DDMMYY')}-${
      number < 10 ? `${'0' + number} ` : number
    }`;
    return {
      id: commandNumber,
      userName: userData?.username,
      time: moment(row.createdAt).format('h:mma - DD/MM/YYYY'),
      parameters: {
        instantHeat: row.newData.on_switch
          ? `active ${
              isF
                ? convertCelsiusToFahrenheit(row.newData.instant_temp)
                : row.newData.instant_temp
            }${unit}`
          : null,
        fanOnly: row.newData.fan ? 'ready' : null,
        optionalConstantTemp: row.newData.on_constant
          ? `ready ${
              isF
                ? convertCelsiusToFahrenheit(row.newData.constant_temp)
                : row.newData.constant_temp
            }${unit}`
          : null,
        snowSensor: row.newData.snow_enabled ? 'ready' : null,
        heatingSchedule: row.newData.schedule?.startDate
          ? `ready ${
              isF
                ? convertCelsiusToFahrenheit(row.newData.schedule.threshold)
                : row.newData.schedule.threshold
            }${unit}` +
            `|${row.newData.schedule.startDate}` +
            `|${row.newData.schedule.endDate}`
          : null,
        windFactor: row.newData.wind ? 'ready' : null,
      },
    };
  });
};

export const machineAuditTrailFaultsFormat = (faultsArray, swtStatus) => {
  const Formatedfaults = [];
  if (!faultsArray) return [];
  faultsArray?.forEach(({ row, ...restProps }) => {
    row?.deviceIds?.forEach((deviceId) => {
      const time = moment(row.createdAt).format('h:mma - DD/MM/YYYY');
      const address =
        getLocationDataByDeviceId(deviceId, swtStatus)?.locationAddress ?? '';
      const attends = row?.userAttends ?? [];
      const userName = attends[0]?.user_name ?? '';
      const comment = attends.map(
        (attend) =>
          `- ${attend?.user_name} / ${attend?.action_taken} - ${moment(
            attend?.timestamp,
          ).format('h:mma - DD/MM/YYYY')}`,
      );
      const buildFault = (fault, number = []) => ({
        fault,
        number,
        userName,
        title: `${fault}${
          row.newData?.position !== undefined
            ? ` (${row.newData.position + 1})`
            : ''
        } - mbta-${address}`,
        comment,
        time,
      });
      if (
        row?.newData?.hasOwnProperty('e_ssr_fault') &&
        row.newData?.e_ssr_fault?.includes(1)
      ) {
        Formatedfaults.push(
          buildFault('ssr fault', getFaultIndexes(row.newData?.e_ssr_fault)),
        );
      }

      if (
        row?.newData?.hasOwnProperty('e_thermocouple_fault') &&
        row.newData?.e_thermocouple_fault?.includes(1)
      ) {
        Formatedfaults.push(
          buildFault(
            'thermocouple fault',
            getFaultIndexes(row.newData?.e_thermocouple_fault),
          ),
        );
      }
      if (
        row?.newData?.hasOwnProperty('e_ground_fault') &&
        row.newData?.e_ground_fault == 1
      ) {
        Formatedfaults.push(buildFault('ground fault'));
      }

      if (
        row?.newData?.hasOwnProperty('e_srr_over_current') &&
        row.newData?.e_srr_over_current?.includes(1)
      ) {
        Formatedfaults.push(
          buildFault(
            'ssr over current',
            getFaultIndexes(row.newData?.e_srr_over_current),
          ),
        );
      }

      if (
        row?.newData?.hasOwnProperty('ssr_fault') &&
        row.newData?.ssr_fault?.includes(1)
      ) {
        Formatedfaults.push(
          buildFault('ssr fault', getFaultIndexes(row.newData?.ssr_fault)),
        );
      }

      if (
        Array.isArray(row?.newData?.thermocouple_fault) &&
        row.newData.thermocouple_fault.includes(1)
      ) {
        Formatedfaults.push(
          buildFault(
            'thermocouple failure',
            getFaultIndexes(row.newData?.thermocouple_fault),
          ),
        );
      }
      if (
        row?.newData?.hasOwnProperty('ground_fault') &&
        row.newData?.ground_fault == 1
      ) {
        Formatedfaults.push(buildFault('ground fault'));
      }

      if (
        row?.newData?.hasOwnProperty('srr_over_current') &&
        row.newData?.srr_over_current?.includes(1)
      ) {
        Formatedfaults.push(
          buildFault(
            'ssr over current',
            getFaultIndexes(row.newData?.srr_over_current),
          ),
        );
      }

      if (
        row?.newData?.hasOwnProperty('bms_fault') &&
        row.newData?.bms_fault == 1
      ) {
        Formatedfaults.push(buildFault('bms fault'));
      }

      if (
        row?.newData?.hasOwnProperty('hplp_fault') &&
        row.newData?.hplp_fault == 1
      ) {
        Formatedfaults.push(buildFault('hp/lp fault'));
      }
      if (
        row?.newData?.thermocouple_fault === 1
      ) {
        Formatedfaults.push(buildFault('thermocouple fault'));
      }

      if (
        row?.newData?.hasOwnProperty('timeout_fault') &&
        row.newData?.timeout_fault == 1
      ) {
        Formatedfaults.push(buildFault('timeout fault'));
      }
    });
  });
  return Formatedfaults;
};

export const countdownTimer = (time) => {
  const duration = moment.duration(time, 'seconds');
  const countdown = {
    hours:
      duration._data.hours + duration._data.days * 24 < 10
        ? '0' + duration.hours()
        : duration._data.hours + duration._data.days * 24,
    minutes:
      duration.minutes() < 10 ? '0' + duration.minutes() : duration.minutes(),
    seconds:
      duration.seconds() < 10 ? '0' + duration.seconds() : duration.seconds(),
  };
  return `${countdown.hours}:${countdown.minutes}:${countdown.seconds}`;
};

export const getGraphTypeKey = (graphType) => {
  let result;
  switch (graphType) {
    case GRAPH_TYPES.HEATER:
      result = 'heaterGraphData';
      break;
    case GRAPH_TYPES.ENCLOSURE:
    case GRAPH_TYPES.GAS_ENCLOSURE:
      result = 'enclosureGraphData';
      break;
    case GRAPH_TYPES.OUTSIDE:
      result = 'outsideGraphData';
      break;
    case GRAPH_TYPES.GAS:
      result = 'gasGraphData';
      break;
    case GRAPH_TYPES.SNOW:
    case GRAPH_TYPES.GAS_SNOW:
      result = 'snowGraphData';
      break;
    case GRAPH_TYPES.WIND:
    case GRAPH_TYPES.GAS_WIND:
      result = 'windGraphData';
      break;
    case 'energy':
    case 'fuel':
      result = 'energyGasConsumptionGraphData';
      break;
  }
  return result;
};

export const formatDateXAxis = (intervalUnit) => {
  let result;
  switch (intervalUnit) {
    case 'months':
      result = 'MMM';
      break;
    case 'weeks':
      result = 'ww';
    case 'days':
      result = 'DD';
      break;
    case 'hours':
      result = 'HH';
      break;
    case 'minutes':
      result = 'mm';
      break;
  }
  return result;
};

const CHART_BAR_LABEL_FORMATS = {
  months: 'MMM',
  weeks: 'MMM',
  days: 'DD',
  hours: 'HH:mm',
  minutes: 'HH:mm',
};

export const formatChartBarLabel = (id, intervalUnit) => {
  if (id === null || id === undefined) return '';

  const period = moment(id);
  return period.isValid()
    ? period.format(CHART_BAR_LABEL_FORMATS[intervalUnit] ?? 'MMM')
    : '';
};

const CHART_TIME_AXIS_UNITS = {
  months: 'MONTH',
  weeks: 'WEEK',
  days: 'DAY',
  hours: 'HOUR',
  minutes: 'MINUTE',
};

export const chartTimeAxisTitle = (intervalUnit) =>
  `TIME (${CHART_TIME_AXIS_UNITS[intervalUnit] ?? 'DAY'})`;

export const formatDateTooltip = (intervalUnit) => {
  let result;
  switch (intervalUnit) {
    case 'months':
    case 'weeks':
      result = 'MMMM YYYY';
      break;
    case 'days':
      result = 'MMMM dddd DD, YYYY';
      break;
    case 'hours':
    case 'minutes':
      result = 'MMMM dddd DD, YYYY - hh:mm a';
      break;
  }
  return result;
};

export const getAuditTrailActionTitle = (
  actionType,
  deviceType,
  isLCD = null,
) => {
  const actionTypeTitles = {
    MASTER_CONTROL: {
      ESS: 'ESS-MASTER CONTROL ACTION',
      TES: 'TES-MASTER CONTROL ACTION',
      default: 'TGS-MASTER CONTROL ACTION',
    },
    GLOBAL_MASTER_CONTROL: 'MASTER CONTROL ACTION',
    FAST_MASTER_CONTROL: {
      ESS: 'ESS-QUICK MASTER CONTROL ACTION',
      TES: 'TES-QUICK MASTER CONTROL ACTION',
      default: 'TGS-QUICK MASTER CONTROL ACTION',
    },
    LOCAL_MASTER_CONTROL: {
      ESS: 'ESS-LOCATION CONTROL ACTION',
      TES: 'TES-LOCATION CONTROL ACTION',
      default: 'TGS-LOCATION CONTROL ACTION',
    },
    SSR_RESET: isLCD ? 'SSR FAULT LCD ACTION' : 'SSR FAULT ACTION',
    THERMOCOUPLE_RESET: isLCD
      ? 'THERMOCOUPLE FAILURE LCD ACTION'
      : 'THERMOCOUPLE FAILURE ACTION',
    GROUND_RESET: isLCD ? 'GROUND FAULT LCD ACTION' : 'GROUND FAULT ACTION',
    LOAD_EXCEED_RESET: isLCD
      ? 'SSR OVER CURRENT FAULT LCD ACTION'
      : 'SSR OVER CURRENT FAULT ACTION',
    BMS_RESET: isLCD ? 'BMS FAULT LCD ACTION' : 'BMS FAULT ACTION',
    TIMEOUT_RESET: isLCD
      ? 'TIME OUT FAULT LCD ACTION'
      : 'TIME OUT FAULT ACTION',
    HPLP_RESET: isLCD
      ? 'HIGH PRESSURE/LOW PRESSURE FAULT LCD ACTION'
      : 'HIGH PRESSURE/LOW PRESSURE FAULT ACTION',
    GLOBAL_TELEMETRY_OVERVIEW: 'GLOBAL SYSTEM TELEMETRY OVERVIEW ACTION',
    GLOBAL_DATA_TELEMETRY_OVERVIEW: 'GLOBAL SYSTEM TELEMETRY OVERVIEW ACTION',
    AUDIT_TRAIL: 'AUDIT TRAIL ACTION',
    ACTION: {
      ESS: isLCD ? 'ESS-LCD CONTROL ACTION' : 'ESS-SWITCH CONTROL ACTION',
      TES: isLCD ? 'TES-LCD CONTROL ACTION' : 'TES-SWITCH CONTROL ACTION',
      default: isLCD ? 'TGS-LCD CONTROL ACTION' : 'TGS-SWITCH CONTROL ACTION',
    },
    SETTING: 'SETTINGS ACTION',
    HEAT_CONFIGURATION: 'SETTINGS HEAT LOAD CONFIGURATION ACTION',
  };

  const actionTitle = actionTypeTitles[actionType] || '';

  if (typeof actionTitle === 'string') {
    return actionTitle;
  } else if (typeof actionTitle === 'object') {
    return actionTitle[deviceType] || actionTitle.default || '';
  }

  return '';
};

export const createCommandTitle = (
  actionType = '',
  deviceType = '',
  isLCD = null,
) => {
  const actionTypeTitles = {
    MASTER_CONTROL: {
      ESS: 'ESS-MC/A',
      TES: 'TES-MC/A',
      default: 'TGS-MC/A',
    },
    GLOBAL_MASTER_CONTROL: 'MC/A',
    FAST_MASTER_CONTROL: {
      ESS: 'ESS-QUICK/MC/A',
      TES: 'TES-QUICK/MC/A',
      default: 'TGS-QUICK/MC/A',
    },
    LOCAL_MASTER_CONTROL: {
      ESS: 'ESS-LOC_MC/A',
      TES: 'TES-LOC_MC/A',
      default: 'TGS-LOC_MC/A',
    },
    SSR_RESET: isLCD ? 'SSR_F/LCD/A' : 'SSR_F/A',
    LOAD_EXCEED_RESET: isLCD ? 'SSR_OC_F/LCD/A' : 'SSR_OC_F/A',
    BMS_RESET: isLCD ? 'BMS_F/LCD/A' : 'BMS_F/A',
    THERMOCOUPLE_RESET: isLCD ? 'T/C_F/LCD/A' : 'T/C_F/A',
    GROUND_RESET: isLCD ? 'GROUND_F/LCD/A' : 'GROUND_F/A',
    TIMEOUT_RESET: isLCD ? 'T_O_F/LCD/A' : 'T_O_F/A',
    HPLP_RESET: isLCD ? 'HP/LP_F/LCD/A' : 'HP/LP_F/A',
    GLOBAL_TELEMETRY_OVERVIEW: 'GSTO/A',
    GLOBAL_DATA_TELEMETRY_OVERVIEW: 'GSTO_DATA/A',
    AUDIT_TRAIL: 'AT/A',
    ACTION: {
      ESS: 'ESS-SWT_C/A',
      TES: 'TES-SWT_C/A',
      default: 'TGS-SWT_C/A',
    },
    SETTING: 'SET/A',
    HEAT_CONFIGURATION: 'SET/A',
  };

  const actionTitle = actionTypeTitles[actionType] || '';

  if (typeof actionTitle === 'string') {
    return actionTitle;
  } else if (typeof actionTitle === 'object') {
    return actionTitle[deviceType] || actionTitle.default || '';
  }

  return '';
};

const RESET_DETAIL_TITLES = {
  SSR_RESET: 'SSR FAILURE RESET ACTION',
  THERMOCOUPLE_RESET: 'THERMOCOUPLE FAILURE RESET ACTION',
  GROUND_RESET: 'GROUND FAILURE RESET ACTION',
  HPLP_RESET: 'HP/LP FAILURE RESET ACTION',
  TIMEOUT_RESET: 'TIMEOUT FAILURE RESET ACTION',
  BMS_RESET: 'BMS FAILURE RESET ACTION',
  LOAD_EXCEED_RESET: 'SSR OVER CURRENT RESET ACTION',
};

export const resetDetailTitle = (actionType) =>
  RESET_DETAIL_TITLES[actionType] ?? null;

export const getConsumptionValue = (swtName, value, isF) => {
  return swtName === 'tgs'
    ? Math.round((isF ? value : value * 0.0283168) * 10) / 10
    : Math.round(value * 10) / 10;
};

export const getSelectedDeviceIds = (
  masterControlSelects,
  swtSwitch,
  swtName,
) => {
  return masterControlSelects[swtName].isMachineSelected
    .map((arr, idxArr) => {
      return arr.map((el, edxEl) => {
        if (Array.isArray(el)) {
          if (el.some(Boolean)) {
            const devices = Object.values(
              Object.values(swtSwitch)[idxArr].subLocations,
            )[edxEl].devices;

            const deviceKeys = Object.keys(devices);

            return deviceKeys.filter((_, i) => el[i] === true);
          }
        } else if (el) {
          const location = Object.values(swtSwitch)[idxArr]?.devices;

          return Object.keys(location)[edxEl];
        }
        return false;
      });
    })
    .flat()
    .filter((el) => el);
};

const getColorBlockChart = (index, swtName) => {
  const isOdd = index % 2 === 1;
  switch (swtName) {
    case 'ess':
      return isOdd ? '#83FFFF' : '#70C7F0';
    case 'tgs':
      return isOdd ? '#FF7800' : '#FFC400';
    case 'tes':
      return isOdd ? '#95FF45' : '#D5FF45';
  }
};

export const getTotalSearchValues = (data, type, swtName, isF, isDc) => {
  return Math.floor(
    sum(
      data.map((el) => {
        const machine = isDc ? el : el.data;
        return sum(
          machine
            .filter((el) => typeof el.startTime === 'number' || el.start_date)
            .map((el) => {
              switch (type) {
                case 'hourOfUsage':
                  return Math.floor((el.hourOfUsage || 0) / 3600);
                case 'consumption':
                  return getConsumptionValue(swtName, el.consumption, isF);
                case 'total':
                  return Math.floor(el.total || 0);
              }
            }),
        );
      }),
    ),
  );
};

export const getTotalMachineValues = (data, type, swtName, isF) => {
  return Math.floor(
    sum(
      data
        .filter((el) => typeof el.startTime === 'number' || el.start_date)
        .map((el) => {
          switch (type) {
            case 'hourOfUsage':
              return Math.floor((el.hourOfUsage || 0) / 3600);
            case 'consumption':
              return getConsumptionValue(swtName, el.consumption, isF);
            case 'total':
              return Math.floor(el.total || 0);
          }
        }),
    ),
  );
};

export const formatTelemetryChartData = (data, swtName, swtSwitch, isF) => {
  const color =
    swtName === 'ess' ? '#83FFFF' : swtName === 'tgs' ? '#FF7800' : '#95FF45';

  const intervalData = data.map((machine) => {
    const machineData = getLocationDataByDeviceId(machine.deviceId, swtSwitch)[
      machine.deviceId
    ];
    if (isEmpty(machineData)) {
      return {
        machine: machine.deviceId,
        switch: `unassigned`,
        totalConsumption: 0,
        totalUsageHours: 0,
        barColor: color,
      };
    }

    return {
      machine: machine.deviceId,
      switch: `${machineData.locationName} - ${machineData.machineName}`,
      totalConsumption: getTotalMachineValues(
        machine.data,
        'consumption',
        swtName,
        isF,
      ),
      totalUsageHours: getTotalMachineValues(machine.data, 'hourOfUsage'),
      barColor: color,
      ...machine.data
        .filter((el) => typeof el.startTime === 'number')
        .map((el, index) => {
          const intervalName = el.startTime;
          return {
            [intervalName]: getConsumptionValue(swtName, el.consumption, isF),
            [`${intervalName}TEC`]: `${getConsumptionValue(
              swtName,
              el.consumption,
              isF,
            )} kw`,
            [`${intervalName}THU`]: `${Math.floor(
              (el.hourOfUsage || 0) / 3600,
            )} hours`,
            [`${intervalName}Color`]: getColorBlockChart(index, swtName),
          };
        })
        .reduce((cur, result) => {
          return { ...result, ...cur };
        }, {}),
    };
  });

  const intervalKeys = [
    ...new Set(
      data
        .map((machine) => {
          return machine.data
            .filter((el) => typeof el.startTime === 'number')
            .map((el) => el.startTime);
        })
        .flat(),
    ),
  ].sort();

  return {
    intervalData,
    intervalKeys,
  };
};

export const getMachineDataByInhandId = (inhandId, swtSwitch) => {
  for (const location in swtSwitch) {
    for (const machine in swtSwitch[location]) {
      if (swtSwitch[location][machine].inhandId === inhandId) {
        return swtSwitch[location][machine];
      }
    }
  }
};

export const getAllSpecificLocationNames = (data) => {
  const allSpecificLocationsName = [];
  data &&
    Object.values(data).forEach((value) => {
      if (value.isSpecificLocation) {
        allSpecificLocationsName.push(Object.keys(value.subLocations));
      }
    });
  return allSpecificLocationsName;
};

export const getSpecLocationHandler = (data) => {
  return Object.values(data).filter((location) =>
    Object.values(location).some((el) => !el.deviceMac),
  );
};

export const transformSpecificLocationData = (data, scope) => {
  const filteredData = Object.entries(data).filter(
    ([key, value]) => key === scope,
  )[0];
  return { [filteredData[0]]: filteredData[1] };
};

export const getSpecificLocationNameOfALocation = (location) => {
  const allSpecificLocationsName = [];
  location &&
    Object.entries(location).forEach(([key, value]) => {
      if (Object.values(value)[0]?.deviceMac && Object.keys(value).length > 0) {
        allSpecificLocationsName.push(key);
      }
    });
  return allSpecificLocationsName;
};

export const extractSwtSize = (name) => {
  if (name) {
    return name.match(/\d+/g);
  } else {
    return '';
  }
};
export const extractApplicationAbr = (name) => {
  if (name) {
    return name
      .replace(/[^a-zA-Z\s]/g, '')
      .replace(/\s+/g, ' ')
      .split(' ')
      .filter((word) => word && word.trim() !== '')
      .reduce((prev, word) => {
        if (prev) {
          return `${prev}.${word.charAt(0).toLowerCase()}`;
        }
        return word.charAt(0).toLowerCase();
      }, '');
  } else {
    return '';
  }
};

export const filteredSuggestionsHandler = (list, locations, inputValue) => {
  if (!list || !locations.all || !inputValue) {
    return [];
  }

  return list.filter((suggestion) => {
    const parts = suggestion.split(' - ');

    const searchableNames = parts.map((part, index) => {
      if (index > 0 && index === parts.length - 1) {
        return (
          locations.all[parts[index - 1]]?.devices?.[part]?.machineName || ''
        );
      }

      return locations.all[part]?.locationName || '';
    });

    const searchableString = searchableNames
      .filter(Boolean)
      .join(' - ')
      .toUpperCase();
    return searchableString.includes(inputValue.toUpperCase());
  });
};

export const getFormattedMachineName = (swtData) => {
  const locations = Object.keys(swtData).map((location) =>
    Object.entries(swtData[location]).flatMap(([key, el]) => {
      if (el.deviceMac) {
        return `${location} - ${key}`;
      } else {
        return Object.keys(swtData[location][key]).flatMap(
          (machine) => `${location} - ${key} - ${machine}`,
        );
      }
    }),
  );

  return locations.reduce((acc, cur) => acc.concat(cur), []);
};

const extractAllowedSettings = (role) => {
  return Object.keys(ROLE_PERMISSIONS.ALLOWED_SETTINGS).filter((setting) =>
    ROLE_PERMISSIONS.ALLOWED_SETTINGS[setting].includes(role),
  );
};
export const checkUserPermissions = (role) => {
  const permissions = {};
  for (const [key, value] of Object.entries(ROLE_PERMISSIONS)) {
    if (key === 'ALLOWED_SETTINGS') {
      permissions[key] = extractAllowedSettings(role);
    } else {
      permissions[key] = value.includes(role);
    }
  }
  return permissions;
};
export const getWifiAlertStatusHandler = (
  switchData,
  accessKey,
  location,
  noSpecificLocation,
  accessSpecificLocation,
) => {
  const gpEbpArray = [];
  let gpEbpState = null;

  if (noSpecificLocation) {
    let systemData;
    if (accessSpecificLocation) {
      systemData = location?.[1]?.[switchData] || [];
    } else {
      systemData = Object.values(location || {})?.[0]?.[switchData] || [];
    }

    gpEbpState =
      systemData?.length > 0
        ? systemData
            .map((el) =>
              Object.values(el).some(
                (gpEbp) => gpEbp.gpEbpWifiAlertStatus[accessKey] === true,
              ),
            )
            .some((value) => value === true)
        : null;
  } else {
    gpEbpState = Object.values(location)
      .flatMap((specificLocationEl) => {
        const systemData = Object.values(specificLocationEl)[0][switchData];

        return systemData?.length > 0
          ? systemData
              .map(
                (machine) =>
                  Object.values(machine)[0].gpEbpWifiAlertStatus[accessKey],
              )
              .some((status) => status === true)
          : null;
      })
      .some((value) => value === true);
  }

  if (gpEbpState !== null) {
    gpEbpArray.push(gpEbpState);
  }
  return gpEbpArray;
};

export const getFaultsAlertsHandler = (data) => {
  const filteredData = data.filter((switchEl) =>
    switchEl[1].machines
      ? switchEl[1].machines.length > 0
      : switchEl[1].length > 0,
  );

  const faultsState = filteredData.map((el) => {
    return el[1].some((el) => {
      if (el.machines) {
        return el.machines.some((machine) => {
          return (
            Object.values(machine)[1].gpEbpWifiAlertStatus.isFault === true
          );
        });
      } else {
        return Object.values(el)[0].gpEbpWifiAlertStatus.isFault === true;
      }
    });
  });

  return { faultsState, filteredData };
};

const DATA_CONSUMPTION_DIVISORS = {
  mb: 1024 * 1024,
  gb: 1024 * 1024 * 1024,
};

export const dataConsumptionUnit = (value) =>
  isNumber(value) && Math.abs(value) >= DATA_CONSUMPTION_DIVISORS.gb
    ? 'gb'
    : 'mb';

export const dataConsumptionSeriesUnit = (values) =>
  dataConsumptionUnit(
    (values ?? [])
      .filter(Number.isFinite)
      .reduce((max, value) => Math.max(max, Math.abs(value)), 0),
  );

export const toDataConsumption = (value, unit = dataConsumptionUnit(value)) =>
  isNumber(value)
    ? value / (DATA_CONSUMPTION_DIVISORS[unit] ?? DATA_CONSUMPTION_DIVISORS.mb)
    : null;

export const formatDataConsumption = (
  value,
  unit = dataConsumptionUnit(value),
) => {
  const converted = toDataConsumption(value, unit);
  return converted === null ? null : converted.toFixed(2);
};

export const isTelemetrySearchReady = (
  isSystemSelected,
  isSwitchSelected,
  scheduleData,
) =>
  Boolean(isSystemSelected) &&
  Boolean(isSwitchSelected) &&
  Boolean(scheduleData?.start?.date) &&
  Boolean(scheduleData?.end?.date);
